'use client';

import { c as _c } from "react/compiler-runtime";
import { type FundingProvider, fundingLocationLabel, type OnrampClient } from '@morfi/onramp-sdk';
import { type FundingCheckout, useFundingProfile, useFundingProfileContact } from '@morfi/onramp-sdk/react';
import type { MoonPayWebProvider } from '@morfi/onramp-sdk/web';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { getCurrencyByCode } from '@/config/currencies';
import { logger } from '@/lib/telemetry/logger';
import { useCurrencyStore } from '@/stores/currency';
import { FundingConnectionRequired } from '../checkout/funding-connection-required';
import { FundingDevtoolsLink } from '../devtools/funding-devtools-link';
import { FundingDevtoolsScreen } from '../devtools/funding-devtools-page';
import { useFundingStage } from '../shared/funding-chrome';
import { FundingLoading } from '../shared/funding-loading';
import { VerificationForm } from '../verification/funding-verification';
import { FundingCurrency } from './funding-currency';
import { FundingDisconnect } from './funding-disconnect';
import { FundingMethodLimits } from './funding-method-limits';
import { FundingProfileAddCard } from './funding-profile-add-card';
import { FundingProfileCards } from './funding-profile-cards';
interface Props {
  client: OnrampClient;
  provider: FundingProvider & Pick<MoonPayWebProvider, 'setupAddCard'>;
  connection: FundingCheckout['profileConnection'];
  connectionPending?: boolean;
  sessionProofId: string;
  currency: string;
  decimals: number;
  verificationError?: boolean;
  canDisconnect: boolean;
  disconnectBusy: boolean;
  onDisconnect(): Promise<boolean>;
  onBack(): void;
  onUpdated(): void;
  onRefreshEligibility(): void;
  onVerify(): void;
  onAuthenticateSandbox?(email: string): Promise<void>;
}
export function FundingProfile(props) {
  const $ = _c(215);
  const {
    connectionPending: t0
  } = props;
  const connectionPending = t0 === undefined ? false : t0;
  const t = useTranslations("onramp");
  const [editing, setEditing] = useState(null);
  const selectedCurrency = useCurrencyStore(_temp);
  let t1;
  if ($[0] !== connectionPending || $[1] !== props.client || $[2] !== props.connection || $[3] !== props.currency || $[4] !== props.decimals || $[5] !== props.provider || $[6] !== props.sessionProofId) {
    t1 = {
      client: props.client,
      sessionProofId: props.sessionProofId,
      currency: props.currency,
      decimals: props.decimals,
      provider: props.provider,
      connection: props.connection,
      connectionPending,
      includeLimits: false
    };
    $[0] = connectionPending;
    $[1] = props.client;
    $[2] = props.connection;
    $[3] = props.currency;
    $[4] = props.decimals;
    $[5] = props.provider;
    $[6] = props.sessionProofId;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const profile = useFundingProfile(t1);
  let t2;
  if ($[8] !== connectionPending || $[9] !== profile.data?.limitsUnavailable || $[10] !== profile.data?.savedCardsUnavailable || $[11] !== profile.error) {
    t2 = () => {
      if (!connectionPending && (profile.error || profile.data?.limitsUnavailable || profile.data?.savedCardsUnavailable)) {
        logger.warn("onramp.profile_failed", {
          failed: true,
          limitsUnavailable: Boolean(profile.data?.limitsUnavailable),
          savedCardsUnavailable: Boolean(profile.data?.savedCardsUnavailable)
        });
      }
    };
    $[8] = connectionPending;
    $[9] = profile.data?.limitsUnavailable;
    $[10] = profile.data?.savedCardsUnavailable;
    $[11] = profile.error;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const t3 = profile.data?.limitsUnavailable;
  const t4 = profile.data?.savedCardsUnavailable;
  let t5;
  if ($[13] !== connectionPending || $[14] !== profile.error || $[15] !== t3 || $[16] !== t4) {
    t5 = [connectionPending, profile.error, t3, t4];
    $[13] = connectionPending;
    $[14] = profile.error;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  useEffect(t2, t5);
  let t6;
  if ($[18] !== t) {
    t6 = t("profile_title");
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  useFundingStage(t6, false, props.onBack, !editing);
  if (editing === "devtools") {
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setEditing(null);
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== connectionPending || $[22] !== profile.loading || $[23] !== profile.refreshing || $[24] !== props.canDisconnect || $[25] !== props.connection || $[26] !== props.disconnectBusy || $[27] !== props.onAuthenticateSandbox || $[28] !== props.verificationError) {
      t8 = <FundingDevtoolsScreen onBack={t7} {...props.onAuthenticateSandbox ? {
        sandboxCustomer: {
          environment: props.connection.quoteContext?.context.environment,
          disabled: !props.canDisconnect || props.disconnectBusy || connectionPending || profile.loading || profile.refreshing || props.connection.canConnect !== true && !props.connection.guest,
          error: props.verificationError === true,
          onAuthenticate: props.onAuthenticateSandbox
        }
      } : {}} />;
      $[21] = connectionPending;
      $[22] = profile.loading;
      $[23] = profile.refreshing;
      $[24] = props.canDisconnect;
      $[25] = props.connection;
      $[26] = props.disconnectBusy;
      $[27] = props.onAuthenticateSandbox;
      $[28] = props.verificationError;
      $[29] = t8;
    } else {
      t8 = $[29];
    }
    return t8;
  }
  if (editing === "currency") {
    let t7;
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingCurrency onBack={() => setEditing(null)} />;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    return t7;
  }
  if (editing === "card") {
    let t7;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setEditing(null);
      $[31] = t7;
    } else {
      t7 = $[31];
    }
    let t8;
    if ($[32] !== profile) {
      t8 = () => {
        setEditing(null);
        profile.refresh();
      };
      $[32] = profile;
      $[33] = t8;
    } else {
      t8 = $[33];
    }
    let t9;
    if ($[34] !== props.provider || $[35] !== props.sessionProofId || $[36] !== t8) {
      t9 = <FundingProfileAddCard key={props.sessionProofId} provider={props.provider} onBack={t7} onComplete={t8} />;
      $[34] = props.provider;
      $[35] = props.sessionProofId;
      $[36] = t8;
      $[37] = t9;
    } else {
      t9 = $[37];
    }
    return t9;
  }
  if (editing) {
    let t7;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setEditing(null);
      $[38] = t7;
    } else {
      t7 = $[38];
    }
    let t8;
    if ($[39] !== profile || $[40] !== props) {
      t8 = () => {
        setEditing(null);
        profile.refresh();
        props.onUpdated();
      };
      $[39] = profile;
      $[40] = props;
      $[41] = t8;
    } else {
      t8 = $[41];
    }
    let t9;
    if ($[42] !== editing || $[43] !== props.client || $[44] !== t8) {
      t9 = <ProfileContact client={props.client} kind={editing} onBack={t7} onComplete={t8} />;
      $[42] = editing;
      $[43] = props.client;
      $[44] = t8;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    return t9;
  }
  if ((connectionPending || profile.loading) && !profile.data) {
    let t7;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FundingLoading />;
      $[46] = t7;
    } else {
      t7 = $[46];
    }
    return t7;
  }
  if (!profile.data) {
    let t7;
    if ($[47] !== t) {
      t7 = t("profile_load_error");
      $[47] = t;
      $[48] = t7;
    } else {
      t7 = $[48];
    }
    let t8;
    if ($[49] !== t7) {
      t8 = <p>{t7}</p>;
      $[49] = t7;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    const t9 = props.onUpdated;
    let t10;
    if ($[51] !== t) {
      t10 = t("retry");
      $[51] = t;
      $[52] = t10;
    } else {
      t10 = $[52];
    }
    let t11;
    if ($[53] !== props.onUpdated || $[54] !== t10) {
      t11 = <AnimatedButton onClick={t9}>{t10}</AnimatedButton>;
      $[53] = props.onUpdated;
      $[54] = t10;
      $[55] = t11;
    } else {
      t11 = $[55];
    }
    let t12;
    if ($[56] !== t11 || $[57] !== t8) {
      t12 = <div role="alert" className="flex flex-col gap-3">{t8}{t11}</div>;
      $[56] = t11;
      $[57] = t8;
      $[58] = t12;
    } else {
      t12 = $[58];
    }
    return t12;
  }
  const {
    payer,
    verification
  } = profile.data;
  const refreshing = connectionPending || profile.loading || profile.refreshing;
  const actionsDisabled = refreshing || Boolean(profile.error);
  const presentation = profile.presentation;
  const guestUnavailable = props.connection.canConnect === true && !props.connection.guest && payer.emailVerified && payer.phoneVerified && (payer.consentRequired === false || payer.consentAccepted);
  let t7;
  if ($[59] !== t) {
    t7 = t("profile_currency_location_title");
    $[59] = t;
    $[60] = t7;
  } else {
    t7 = $[60];
  }
  let t8;
  if ($[61] !== t7) {
    t8 = <h3 className="text-heading-sm">{t7}</h3>;
    $[61] = t7;
    $[62] = t8;
  } else {
    t8 = $[62];
  }
  let t9;
  if ($[63] !== t) {
    t9 = t("profile_currency_title");
    $[63] = t;
    $[64] = t9;
  } else {
    t9 = $[64];
  }
  let t10;
  if ($[65] !== t9) {
    t10 = <span className="text-foreground-secondary">{t9}</span>;
    $[65] = t9;
    $[66] = t10;
  } else {
    t10 = $[66];
  }
  let t11;
  if ($[67] !== selectedCurrency) {
    t11 = getCurrencyByCode(selectedCurrency)?.symbol;
    $[67] = selectedCurrency;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== selectedCurrency || $[70] !== t11) {
    t12 = <span className="ml-auto">{t11}{selectedCurrency}</span>;
    $[69] = selectedCurrency;
    $[70] = t11;
    $[71] = t12;
  } else {
    t12 = $[71];
  }
  let t13;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setEditing("currency");
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  let t14;
  if ($[73] !== t) {
    t14 = t("profile_currency_change");
    $[73] = t;
    $[74] = t14;
  } else {
    t14 = $[74];
  }
  let t15;
  if ($[75] !== t14) {
    t15 = <AnimatedButton variant="tertiary" size="sm" className="px-0 text-caption underline underline-offset-2" onClick={t13}>{t14}</AnimatedButton>;
    $[75] = t14;
    $[76] = t15;
  } else {
    t15 = $[76];
  }
  let t16;
  if ($[77] !== t10 || $[78] !== t12 || $[79] !== t15) {
    t16 = <div className="flex items-center justify-between gap-3 text-caption">{t10}{t12}{t15}</div>;
    $[77] = t10;
    $[78] = t12;
    $[79] = t15;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  let t17;
  if ($[81] !== t) {
    t17 = t("profile_location_title");
    $[81] = t;
    $[82] = t17;
  } else {
    t17 = $[82];
  }
  let t18;
  if ($[83] !== t17) {
    t18 = <span className="text-foreground-secondary">{t17}</span>;
    $[83] = t17;
    $[84] = t18;
  } else {
    t18 = $[84];
  }
  let t19;
  if ($[85] !== props.connection.quoteContext?.context || $[86] !== t) {
    t19 = fundingLocationLabel(props.connection.quoteContext?.context) ?? t("profile_location_unknown");
    $[85] = props.connection.quoteContext?.context;
    $[86] = t;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  let t20;
  if ($[88] !== t19) {
    t20 = <span className="ml-auto">{t19}</span>;
    $[88] = t19;
    $[89] = t20;
  } else {
    t20 = $[89];
  }
  const t21 = props.onRefreshEligibility;
  let t22;
  if ($[90] !== t) {
    t22 = t("profile_location_refresh");
    $[90] = t;
    $[91] = t22;
  } else {
    t22 = $[91];
  }
  let t23;
  if ($[92] !== props.onRefreshEligibility || $[93] !== refreshing || $[94] !== t22) {
    t23 = <AnimatedButton variant="tertiary" size="sm" className="px-0 text-caption underline underline-offset-2" disabled={refreshing} onClick={t21}>{t22}</AnimatedButton>;
    $[92] = props.onRefreshEligibility;
    $[93] = refreshing;
    $[94] = t22;
    $[95] = t23;
  } else {
    t23 = $[95];
  }
  let t24;
  if ($[96] !== t18 || $[97] !== t20 || $[98] !== t23) {
    t24 = <div className="flex items-center justify-between gap-3 text-caption">{t18}{t20}{t23}</div>;
    $[96] = t18;
    $[97] = t20;
    $[98] = t23;
    $[99] = t24;
  } else {
    t24 = $[99];
  }
  let t25;
  if ($[100] !== t16 || $[101] !== t24) {
    t25 = <div className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4">{t16}{t24}</div>;
    $[100] = t16;
    $[101] = t24;
    $[102] = t25;
  } else {
    t25 = $[102];
  }
  let t26;
  if ($[103] !== t25 || $[104] !== t8) {
    t26 = <div className="flex flex-col gap-3">{t8}{t25}</div>;
    $[103] = t25;
    $[104] = t8;
    $[105] = t26;
  } else {
    t26 = $[105];
  }
  let t27;
  if ($[106] !== profile.error || $[107] !== props.verificationError || $[108] !== t) {
    t27 = (props.verificationError || profile.error) && <p role="alert" className="text-caption text-foreground-secondary">{t("profile_load_error")}</p>;
    $[106] = profile.error;
    $[107] = props.verificationError;
    $[108] = t;
    $[109] = t27;
  } else {
    t27 = $[109];
  }
  let t28;
  if ($[110] !== t) {
    t28 = t("profile_connected");
    $[110] = t;
    $[111] = t28;
  } else {
    t28 = $[111];
  }
  let t29;
  if ($[112] !== t28) {
    t29 = <h3 className="text-heading-sm">{t28}</h3>;
    $[112] = t28;
    $[113] = t29;
  } else {
    t29 = $[113];
  }
  let t30;
  if ($[114] !== t) {
    t30 = t("profile_guest");
    $[114] = t;
    $[115] = t30;
  } else {
    t30 = $[115];
  }
  let t31;
  if ($[116] !== t30) {
    t31 = <h4 className="text-heading-xs">{t30}</h4>;
    $[116] = t30;
    $[117] = t31;
  } else {
    t31 = $[117];
  }
  let t32;
  if ($[118] !== t) {
    t32 = t("email_address");
    $[118] = t;
    $[119] = t32;
  } else {
    t32 = $[119];
  }
  let t33;
  if ($[120] !== payer.verifiedEmail || $[121] !== t) {
    t33 = payer.verifiedEmail ?? t("profile_not_verified");
    $[120] = payer.verifiedEmail;
    $[121] = t;
    $[122] = t33;
  } else {
    t33 = $[122];
  }
  let t34;
  if ($[123] !== t) {
    t34 = t("change_email");
    $[123] = t;
    $[124] = t34;
  } else {
    t34 = $[124];
  }
  let t35;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setEditing("email");
    $[125] = t35;
  } else {
    t35 = $[125];
  }
  let t36;
  if ($[126] !== actionsDisabled || $[127] !== t32 || $[128] !== t33 || $[129] !== t34) {
    t36 = <ProfileContactRow label={t32} value={t33} action={t34} disabled={actionsDisabled} onEdit={t35} />;
    $[126] = actionsDisabled;
    $[127] = t32;
    $[128] = t33;
    $[129] = t34;
    $[130] = t36;
  } else {
    t36 = $[130];
  }
  let t37;
  if ($[131] !== t) {
    t37 = t("phone_number");
    $[131] = t;
    $[132] = t37;
  } else {
    t37 = $[132];
  }
  let t38;
  if ($[133] !== payer.verifiedPhone || $[134] !== t) {
    t38 = payer.verifiedPhone ?? t("profile_not_verified");
    $[133] = payer.verifiedPhone;
    $[134] = t;
    $[135] = t38;
  } else {
    t38 = $[135];
  }
  let t39;
  if ($[136] !== t) {
    t39 = t("change_phone");
    $[136] = t;
    $[137] = t39;
  } else {
    t39 = $[137];
  }
  let t40;
  if ($[138] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => setEditing("phone");
    $[138] = t40;
  } else {
    t40 = $[138];
  }
  let t41;
  if ($[139] !== actionsDisabled || $[140] !== t37 || $[141] !== t38 || $[142] !== t39) {
    t41 = <ProfileContactRow label={t37} value={t38} action={t39} disabled={actionsDisabled} onEdit={t40} />;
    $[139] = actionsDisabled;
    $[140] = t37;
    $[141] = t38;
    $[142] = t39;
    $[143] = t41;
  } else {
    t41 = $[143];
  }
  let t42;
  if ($[144] !== guestUnavailable || $[145] !== props.onRefreshEligibility || $[146] !== refreshing) {
    t42 = guestUnavailable && <FundingConnectionRequired bordered={true} reason="guest_unavailable" canAuthenticate={true} onRefresh={props.onRefreshEligibility} busy={refreshing} />;
    $[144] = guestUnavailable;
    $[145] = props.onRefreshEligibility;
    $[146] = refreshing;
    $[147] = t42;
  } else {
    t42 = $[147];
  }
  let t43;
  if ($[148] !== t) {
    t43 = t("profile_kyc_title");
    $[148] = t;
    $[149] = t43;
  } else {
    t43 = $[149];
  }
  let t44;
  if ($[150] !== t43) {
    t44 = <h4 className="border-t border-border-secondary pt-3 text-heading-xs">{t43}</h4>;
    $[150] = t43;
    $[151] = t44;
  } else {
    t44 = $[151];
  }
  let t45;
  if ($[152] !== t || $[153] !== verification) {
    t45 = verification && verification.status !== "active" && <p>{t(`profile_status_${verification.status}`)}</p>;
    $[152] = t;
    $[153] = verification;
    $[154] = t45;
  } else {
    t45 = $[154];
  }
  let t46;
  if ($[155] !== presentation.requirements || $[156] !== t) {
    let t47;
    if ($[158] !== t) {
      t47 = requirement => <div key={requirement.category} className="flex items-center justify-between gap-3"><span className="text-caption">{t(`profile_requirement_${requirement.category}`)}</span><span className="text-caption text-foreground-secondary">{t(`profile_requirement_status_${requirement.status}`)}</span></div>;
      $[158] = t;
      $[159] = t47;
    } else {
      t47 = $[159];
    }
    t46 = presentation.requirements.map(t47);
    $[155] = presentation.requirements;
    $[156] = t;
    $[157] = t46;
  } else {
    t46 = $[157];
  }
  let t47;
  if ($[160] !== actionsDisabled || $[161] !== presentation.canVerify || $[162] !== profile.data.canConnect || $[163] !== props.onVerify || $[164] !== t) {
    t47 = presentation.canVerify && <><AnimatedButton onClick={props.onVerify} disabled={actionsDisabled}>{profile.data?.canConnect ? t("profile_connect_moonpay") : t("profile_complete_verification")}</AnimatedButton><p className="text-caption text-foreground-secondary">{t("profile_higher_limits")}</p></>;
    $[160] = actionsDisabled;
    $[161] = presentation.canVerify;
    $[162] = profile.data.canConnect;
    $[163] = props.onVerify;
    $[164] = t;
    $[165] = t47;
  } else {
    t47 = $[165];
  }
  let t48;
  if ($[166] !== props.canDisconnect || $[167] !== props.connection.verification || $[168] !== props.disconnectBusy || $[169] !== props.onDisconnect || $[170] !== refreshing) {
    t48 = props.connection.verification ? <FundingDisconnect canDisconnect={props.canDisconnect && !refreshing} busy={props.disconnectBusy} onDisconnect={props.onDisconnect} /> : null;
    $[166] = props.canDisconnect;
    $[167] = props.connection.verification;
    $[168] = props.disconnectBusy;
    $[169] = props.onDisconnect;
    $[170] = refreshing;
    $[171] = t48;
  } else {
    t48 = $[171];
  }
  let t49;
  if ($[172] !== t31 || $[173] !== t36 || $[174] !== t41 || $[175] !== t42 || $[176] !== t44 || $[177] !== t45 || $[178] !== t46 || $[179] !== t47 || $[180] !== t48) {
    t49 = <div className="flex flex-col gap-3 rounded-control bg-background-tertiary p-4">{t31}{t36}{t41}{t42}{t44}{t45}{t46}{t47}{t48}</div>;
    $[172] = t31;
    $[173] = t36;
    $[174] = t41;
    $[175] = t42;
    $[176] = t44;
    $[177] = t45;
    $[178] = t46;
    $[179] = t47;
    $[180] = t48;
    $[181] = t49;
  } else {
    t49 = $[181];
  }
  let t50;
  if ($[182] !== t29 || $[183] !== t49) {
    t50 = <div className="flex flex-col gap-3">{t29}{t49}</div>;
    $[182] = t29;
    $[183] = t49;
    $[184] = t50;
  } else {
    t50 = $[184];
  }
  let t51;
  if ($[185] !== profile.data.savedCards || $[186] !== profile.data.savedCardsUnavailable || $[187] !== profile.refresh || $[188] !== props.provider || $[189] !== props.sessionProofId || $[190] !== refreshing || $[191] !== verification) {
    t51 = verification ? <FundingProfileCards key={`${props.sessionProofId}:${verification.customerId}`} provider={props.provider} onAdd={() => setEditing("card")} cards={profile.data.savedCards ?? []} unavailable={profile.data.savedCardsUnavailable ?? false} refreshing={refreshing} onRefresh={profile.refresh} /> : null;
    $[185] = profile.data.savedCards;
    $[186] = profile.data.savedCardsUnavailable;
    $[187] = profile.refresh;
    $[188] = props.provider;
    $[189] = props.sessionProofId;
    $[190] = refreshing;
    $[191] = verification;
    $[192] = t51;
  } else {
    t51 = $[192];
  }
  const t52 = !connectionPending;
  let t53;
  if ($[193] !== props.client || $[194] !== props.connection || $[195] !== props.currency || $[196] !== props.decimals || $[197] !== props.provider || $[198] !== props.sessionProofId || $[199] !== t52) {
    t53 = <FundingMethodLimits client={props.client} provider={props.provider} connection={props.connection} sessionProofId={props.sessionProofId} currency={props.currency} decimals={props.decimals} enabled={t52} />;
    $[193] = props.client;
    $[194] = props.connection;
    $[195] = props.currency;
    $[196] = props.decimals;
    $[197] = props.provider;
    $[198] = props.sessionProofId;
    $[199] = t52;
    $[200] = t53;
  } else {
    t53 = $[200];
  }
  const t54 = props.onUpdated;
  let t55;
  if ($[201] !== t) {
    t55 = t("profile_refresh");
    $[201] = t;
    $[202] = t55;
  } else {
    t55 = $[202];
  }
  let t56;
  if ($[203] !== props.onUpdated || $[204] !== refreshing || $[205] !== t55) {
    t56 = <button type="button" onClick={t54} disabled={refreshing} className="cursor-pointer text-caption text-foreground-secondary underline-offset-4 hover:underline focus-visible:underline disabled:cursor-not-allowed">{t55}</button>;
    $[203] = props.onUpdated;
    $[204] = refreshing;
    $[205] = t55;
    $[206] = t56;
  } else {
    t56 = $[206];
  }
  let t57;
  if ($[207] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <FundingDevtoolsLink onOpen={() => setEditing("devtools")} />;
    $[207] = t57;
  } else {
    t57 = $[207];
  }
  let t58;
  if ($[208] !== t26 || $[209] !== t27 || $[210] !== t50 || $[211] !== t51 || $[212] !== t53 || $[213] !== t56) {
    t58 = <div className="flex flex-col gap-5" data-private="true">{t26}{t27}{t50}{t51}{t53}{t56}{t57}</div>;
    $[208] = t26;
    $[209] = t27;
    $[210] = t50;
    $[211] = t51;
    $[212] = t53;
    $[213] = t56;
    $[214] = t58;
  } else {
    t58 = $[214];
  }
  return t58;
}
function _temp(state) {
  return state.currency;
}
function ProfileContactRow(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    action,
    disabled,
    onEdit
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <p className="text-caption text-foreground-secondary">{label}</p>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <p className="break-all text-caption">{value}</p>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== action || $[8] !== disabled || $[9] !== onEdit) {
    t4 = <AnimatedButton variant="tertiary" size="sm" className="shrink-0 text-caption underline" onClick={onEdit} disabled={disabled}>{action}</AnimatedButton>;
    $[7] = action;
    $[8] = disabled;
    $[9] = onEdit;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex items-center justify-between gap-3">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function ProfileContact(t0) {
  const $ = _c(7);
  const {
    client,
    kind,
    onBack,
    onComplete
  } = t0;
  const verification = useFundingProfileContact(client, kind);
  const completed = useRef(false);
  let t1;
  let t2;
  if ($[0] !== onComplete || $[1] !== verification.step) {
    t1 = () => {
      if (verification.step === "ready" && !completed.current) {
        completed.current = true;
        onComplete();
      }
    };
    t2 = [verification.step, onComplete];
    $[0] = onComplete;
    $[1] = verification.step;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onBack || $[5] !== verification) {
    t3 = <VerificationForm verification={verification} requireConsent={false} onBack={onBack} />;
    $[4] = onBack;
    $[5] = verification;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}